import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { API_BASE_URL } from '@/shared/lib/env';
import { logger } from '@/shared/lib/logger';
import { getSessionStorage, tokenManager } from '@/shared/lib/storage';
import { isDebugEnabled } from '@/shared/lib/system';
import { toast } from '@/shared/ui';

/**
 * AIトークン制限の対象となるエンドポイントパターン
 * バックエンドで @require_token_limit() が適用されているエンドポイントに対応
 */
const AI_TOKEN_LIMIT_ENDPOINT_PATTERNS = [
  /\/comparisons\/[^/]+\/mappings$/, // 査定AI一括マッピング
  /\/reference_chats\//, // リファレンスチャット作成（特別交付税マッチング含む）
  /\/uploads\/extractions$/, // PDF解析
  /\/uploads$/, // Presigned URL取得
];

/**
 * デフォルトのタイムアウト（30秒）
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * AI処理など長時間かかるエンドポイント用のタイムアウト（1分）
 */
const LONG_TIMEOUT = 60000;

/**
 * 長時間タイムアウトを適用するエンドポイントパターン
 */
const LONG_TIMEOUT_ENDPOINT_PATTERNS = [
  /\/uploads\/extractions$/, // PDF解析（Bedrock処理）
];

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  // 配列パラメータを正しくシリアライズ（fields=tag1&fields=tag2 形式）
  paramsSerializer: {
    indexes: null, // fields[0]=tag ではなく fields=tag の形式
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // セキュアなトークン管理を使用
    const token = tokenManager.getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // AI処理など長時間かかるエンドポイントはタイムアウトを延長
    if (
      config.url &&
      LONG_TIMEOUT_ENDPOINT_PATTERNS.some((pattern) => pattern.test(config.url ?? ''))
    ) {
      config.timeout = LONG_TIMEOUT;
    }

    // デバッグモード: AIトークンエラーモードがONの場合、AI系エンドポイントへのPOSTを擬似402で拒否
    if (
      isDebugEnabled() &&
      config.method === 'post' &&
      config.url &&
      getSessionStorage('debug-ai-token-error-mode') &&
      AI_TOKEN_LIMIT_ENDPOINT_PATTERNS.some((pattern) => pattern.test(config.url ?? ''))
    ) {
      const error = new axios.AxiosError(
        'Token usage limit exceeded',
        'ERR_BAD_REQUEST',
        config,
        undefined,
        {
          status: 402,
          statusText: 'Payment Required',
          headers: {},
          config,
          data: { error_code: 'TOKEN_LIMIT_EXCEEDED', error_msg: 'Token usage limit exceeded' },
        } as never,
      );
      return Promise.reject(error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    const traceLog = [response.config.method?.toUpperCase(), response.config.url];
    logger.trace(traceLog.join(' '), {
      query: response.config.params,
      body: response.config.data,
      data: response.data,
    });

    // JSONを期待しているのにHTMLが返ってきた場合（SPAフォールバックで index.html が返された場合）をエラーとして扱う
    const contentType = response.headers?.['content-type'];
    if (typeof contentType === 'string' && contentType.includes('text/html')) {
      const error = new axios.AxiosError(
        'APIがHTMLレスポンスを返しました。リソースが見つかりません。',
        'ERR_INVALID_RESPONSE',
        response.config,
        response.request,
        { ...response, status: 404 },
      );
      return Promise.reject(error);
    }

    return response;
  },
  (error: AxiosError) => {
    const originalStackTrace = error.stack;
    if (originalStackTrace) {
      error.stack = `${error.stack}\nCaused by:\n${originalStackTrace.split('\n').slice(2).join('\n')}`;
    }
    // リクエストキャンセルは正常な動作なので無視
    if (error.message === 'canceled' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    // 🔐 401認証エラーは suppressToast に関わらず常にトークンクリア＋リダイレクトを実行
    if (error.response?.status === 401) {
      tokenManager.clearTokens();

      // ログインページ以外からの401エラーの場合のみリダイレクト
      const currentPath = window.location.pathname;
      const loginPath = '/login';
      if (currentPath !== loginPath) {
        // ログインページへリダイレクト（現在のURLとエラー情報を保存）
        const currentUrl = window.location.pathname + window.location.search;
        window.location.href = `${loginPath}?redirect=${encodeURIComponent(currentUrl)}&auth_error=session_expired`;
      }

      return Promise.reject(error);
    }

    // 呼び出し元がエラーハンドリングを制御する場合、toast表示をスキップ
    if (error.config?.suppressToast) {
      return Promise.reject(error);
    }

    // Handle errors
    // TanStack Queryのリトライ時に同一エラーのtoastが重複表示されないよう、リクエスト情報からIDを生成
    const toastId =
      error.config?.method && error.config?.url
        ? `${error.config.method}-${error.config.url}-${error.response?.status ?? 'network'}`
        : `${Date.now()}-${error.response?.status ?? 'network'}`;

    if (error.response) {
      switch (error.response.status) {
        case 402: {
          const errorData = error.response.data as { error_code?: string };
          if (errorData.error_code === 'TOKEN_LIMIT_EXCEEDED') {
            toast.error('AIクレジットが上限に達しました。', { id: toastId });
          } else {
            toast.error('エラーが発生しました。', { id: toastId });
          }
          break;
        }
        case 403:
          toast.error('アクセス権限がありません。', { id: toastId });
          break;
        case 404:
          toast.error('リソースが見つかりません。', { id: toastId });
          break;
        case 500:
          toast.error('サーバーエラーが発生しました。', { id: toastId });
          break;
        default: {
          const errorData = error.response.data as { error_code?: string };
          if (errorData.error_code) {
            switch (errorData.error_code) {
              case 'INTEGRITY_ERROR':
                toast.error('使用中のデータのため削除できません');
                break;
            }
          }

          // 400 Bad Request などの場合、サーバーからのメッセージを優先
          toast.error('エラーが発生しました。', { id: toastId });
        }
      }
    } else if (error.request) {
      toast.error('ネットワークエラーが発生しました。', { id: toastId });
    } else {
      toast.error('エラーが発生しました。', { id: toastId });
    }
    return Promise.reject(error);
  },
);

// API response types
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  status: 'success' | 'error';
}

export interface PaginatedResponse<T = unknown> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}
